const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || '';

// Helper to safely parse JSON response
async function parseResponse(response) {
  const data = await response.json().catch(() => ({}));
  
  if (!response.ok) {
    const error = new Error(data.message || response.statusText || 'Request failed');
    error.status = response.status;
    error.response = data;
    
    // Handle 401 Unauthorized - clear auth and reload
    if (response.status === 401) {
      console.warn('Authentication required, clearing auth data...');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      window.location.reload();
    }
    
    throw error;
  }
  
  return data;
}

// Get auth token with validation
function getAuthToken() {
  const token = localStorage?.getItem('auth_token');
  if (!token) {
    console.warn('No auth token found in localStorage');
  }
  return token;
}

export async function apiFetch(path, options = {}) {
  const token = getAuthToken();
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
  
  // Add auth header if token exists
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  // Merge headers, allowing overrides
  const headers = {
    ...defaultHeaders,
    ...(options.headers || {})
  };

  const config = {
    ...options,
    headers,
    credentials: 'include',
    mode: 'cors'
  };

  // Stringify body if it's an object/array and not already a string
  if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
    config.body = JSON.stringify(options.body);
  }

  try {
    const fullUrl = `${API_BASE_URL}${path}`.replace(/([^:]\/)\/+/g, '$1'); // Remove double slashes
    console.debug(`API Request: ${options.method || 'GET'} ${fullUrl}`, { options });
    
    const response = await fetch(fullUrl, config);
    return await parseResponse(response);
  } catch (error) {
    console.error(`API Error (${options.method || 'GET'} ${path}):`, error);
    
    // Enhance error with more context
    if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      error.message = 'Network error. Please check your internet connection.';
    }
    
    throw error;
  }
}

// Helper methods for common HTTP methods
export const api = {
  get: (path, options = {}) => apiFetch(path, { ...options, method: 'GET' }),
  post: (path, body, options = {}) => apiFetch(path, { ...options, method: 'POST', body }),
  put: (path, body, options = {}) => apiFetch(path, { ...options, method: 'PUT', body }),
  patch: (path, body, options = {}) => apiFetch(path, { ...options, method: 'PATCH', body }),
  delete: (path, options = {}) => apiFetch(path, { ...options, method: 'DELETE' })
};

export default api;
